import React, { useCallback, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Typography, CircularProgress, Skeleton } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CategoryItem, CategoryType } from '../../types';
import { getUploadUrl } from '../../config/api';
import ImageCarousel from '../ImageCarousel';
import SafeImage from '../SafeImage';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useCursorContext } from '../../providers/CursorProvider';
import './Gallery.css';

const getYouTubePreviewUrl = (url?: string): string => {
    if (!url) return '';

    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);
    const videoId = match ? match[1] : null;

    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
};

// Helper to get dynamic size class based on index for masonry effect
const getSizeClass = (index: number) => {
    // Pattern: mostly rectangles with varying heights, very few tall items to avoid gaps
    const patterns = [
        'wide',       // 2x1 horizontal rectangle
        'square',     // 1x1 square
        'wide-tall',  // 2x1.5 wider horizontal
        'square',     // 1x1 square
        'wide',       // 2x1 horizontal rectangle
        'wide-short', // 2x0.75 shorter horizontal
        'square',     // 1x1 square
        'wide',       // 2x1 horizontal rectangle
        'square',     // 1x1 square
        'wide-tall',  // 2x1.5 wider horizontal
        'wide',       // 2x1 horizontal rectangle
        'wide-short', // 2x0.75 shorter horizontal
        'square',     // 1x1 square
        'wide',       // 2x1 horizontal rectangle
        'tall',       // 1x2 vertical rectangle (rare, only 1 in 15)
        'square',     // 1x1 square
    ];
    return patterns[index % patterns.length];
};

// Detect if an item is a vertical (portrait) video — e.g. YouTube Shorts or portrait media
const isVerticalVideo = (item: CategoryItem): boolean => {
    if (item.is_youtube && item.youtube_url) {
        return /youtube\.com\/shorts\//i.test(item.youtube_url);
    }
    if (item.media?.width && item.media?.height) {
        return item.media.height > item.media.width;
    }
    return false;
};

// LazyLoadImage component with React.memo and intersection observer for performance
const LazyLoadImage = React.memo<{
    item: CategoryItem;
    alt: string;
    index: number;
    onImageClick: (index: number) => void;
    t: (key: string, fallback: string) => string;
    sizeClass?: string;
}>(({ item, alt, index, onImageClick, t, sizeClass = 'square' }) => {
    const { elementRef, isIntersecting } = useIntersectionObserver({
        threshold: 0.1,
        rootMargin: '100px',
        triggerOnce: true
    });
    const { setCursorType } = useCursorContext();
    const [detectedVertical, setDetectedVertical] = useState<boolean | null>(null);
    const {
        url,
        description,
        title,
        is_video = false,
        is_youtube = false,
        youtube_url,
        url_360 = "",
        is_360 = false,
        type,
        media
    } = item;

    // Compute a 360 flag if either is_360 is true, or if url_360 or url contains ".html"
    const computedIs360 = is_360 ||
        (url_360 && url_360.toLowerCase().includes('.html')) ||
        (url && url.toLowerCase().includes('index.html'));

    const handleClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onImageClick(index);
    }, [index, onImageClick]);

    const handleMouseEnter = useCallback(() => {
        setCursorType('pointer');
    }, [setCursorType]);

    const handleMouseLeave = useCallback(() => {
        setCursorType('default');
    }, [setCursorType]);

    const imageUrl = media?.url ? getUploadUrl(media.url) : (is_youtube ? youtube_url : url);
    const youtubePreviewUrl = is_youtube ? getYouTubePreviewUrl(youtube_url) : '';
    const vertical = detectedVertical ?? isVerticalVideo(item);
    const effectiveAspectRatio = vertical ? '9/16'
        : sizeClass === 'wide' ? '2/1'
        : sizeClass === 'wide-tall' ? '2/1.5'
        : sizeClass === 'wide-short' ? '2/0.75'
        : sizeClass === 'tall' ? '1/2'
        : '1/1';

    const handleVideoMetadata = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
        const v = e.currentTarget;
        if (v.videoWidth && v.videoHeight) {
            setDetectedVertical(v.videoHeight > v.videoWidth);
        }
    }, []);

    return (
        <div
            ref={elementRef}
            className="gallery-item"
        >
            <Box
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                sx={{
                    position: 'relative',
                    aspectRatio: effectiveAspectRatio,
                    overflow: 'hidden',
                    // borderRadius: { xs: 1, sm: 2 },
                    borderRadius: 0,
                    boxShadow: { xs: 1, sm: 3 },
                    cursor: 'none',
                    minWidth: 0,
                    width: '100%',
                    willChange: 'transform',
                    '&:hover': {
                        transition: 'transform 0.3s ease'
                    },
                    '&:hover .overlay': {
                        opacity: { xs: 0, sm: 1 }
                    }
                }}
            >
                {/* Media Content */}
                {!isIntersecting ? (
                    // Show skeleton while not in view
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.1)',
                            transform: 'none'
                        }}
                    />
                ) : is_video && !is_youtube ? (
                    <video
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                        preload="metadata"
                        muted
                        playsInline
                        crossOrigin="anonymous"
                        loop
                        onLoadedMetadata={handleVideoMetadata}
                        onMouseEnter={(e) => e.currentTarget.play()}
                        onMouseLeave={(e) => e.currentTarget.pause()}
                    >
                        <source src={imageUrl} type="video/mp4" />
                    </video>
                ) : is_video ? (
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0, 0, 0, 0.08)'
                        }}
                    >
                        {youtubePreviewUrl ? (
                            <SafeImage
                                src={youtubePreviewUrl}
                                alt={`${alt} YouTube preview`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                                loading="lazy"
                            />
                        ) : (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase'
                                }}
                            >
                                YouTube
                            </Typography>
                        )}
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.35) 100%)'
                            }}
                        >
                            <PlayArrow
                                sx={{
                                    color: 'white',
                                    fontSize: 52,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    borderRadius: '50%',
                                    p: 0.75,
                                    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.28)'
                                }}
                            />
                        </Box>
                    </Box>
                ) : (
                    <SafeImage
                        src={imageUrl}
                        alt={alt}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                        loading="lazy"
                    />
                )}

                {/* Overlay - only show when content is loaded */}
                {isIntersecting && (
                    <Box
                        className="overlay"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            color: 'white',
                            textAlign: 'center',
                            padding: 2
                        }}
                    >
                        <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{
                                fontSize: { xs: '0.875rem', sm: '1.25rem' },
                                lineHeight: { xs: 1.2, sm: 1.6 }
                            }}
                        >
                            {title}
                        </Typography>
                        {description && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    lineHeight: { xs: 1.2, sm: 1.43 }
                                }}
                            >
                                {description}
                            </Typography>
                        )}
                        {computedIs360 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    mt: 1,
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    padding: { xs: '2px 4px', sm: '4px 8px' },
                                    borderRadius: 1,
                                    fontSize: { xs: '0.6rem', sm: '0.75rem' }
                                }}
                            >
                                {t('360_view', '')}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </div>
    );
});

LazyLoadImage.displayName = 'LazyLoadImage';

const InfiniteGallery: React.FC = () => {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState<CategoryType | 'all'>('all');
    const [carouselOpen, setCarouselOpen] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [isSticky, setIsSticky] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const filterSentinelRef = useRef<HTMLDivElement>(null);

    // Use infinite scroll hook
    const {
        items,
        loading,
        error,
        hasMore,
        total,
        loadMore,
        reset,
        setObserverRef
    } = useInfiniteScroll({
        type: selectedCategory,
        pageSize: 20, // Load 20 items at a time
        threshold: 300 // Trigger load when 300px from bottom
    });

    const categoryTypes: CategoryType[] = [
        'exterior', 'interior', 'products', 'animations', 'panoramic360',
    ];

    // Set up intersection observer for infinite scroll
    useEffect(() => {
        if (sentinelRef.current) {
            setObserverRef(sentinelRef.current);
        }
    }, [setObserverRef, hasMore]);

    // Sticky filter: observe the sentinel placed where the inline filter is
    useEffect(() => {
        const el = filterSentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setIsSticky(!entry.isIntersecting),
            { threshold: 0, rootMargin: '-52px 0px 0px 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const handleImageClick = useCallback((index: number) => {
        setCarouselIndex(index);
        setCarouselOpen(true);
    }, []);

    const handleCarouselClose = () => {
        setCarouselOpen(false);
    };

    const handleCategoryChange = useCallback((category: CategoryType | 'all') => {
        setSelectedCategory(category);
        // Reset will be automatically called by the hook when type changes
    }, []);

    if (error && items.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    textAlign: 'center'
                }}
            >
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            paddingB: {
                xs: 1, // Minimal padding on very small screens (320px)
                sm: 2,
                md: 4
            },
            paddingX: 0,
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box'
        }}>
            {/* Sentinel: sits at the top of the gallery; when it leaves the viewport the filter goes fixed */}
            <Box ref={filterSentinelRef} sx={{ height: 0, margin: 0 }} />

            {/* Category Filter — inline (static) until scrolled past, then fixed in header */}
            {(() => {
                const filterButtons = (
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'nowrap', gap: { xs: 0.5, sm: 1 } }}>
                        <Button
                            variant="text"
                            onClick={() => handleCategoryChange('all')}
                            size="small"
                            sx={{
                                textTransform: 'capitalize',
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: '64px' },
                                padding: { xs: '4px', sm: '6px 16px' },
                                height: { xs: '28px', sm: '36px' },
                                color: selectedCategory === 'all' ? '#000' : '#ADADAD',
                                textDecoration: selectedCategory === 'all' ? 'underline' : 'none',
                                '&:hover': { color: '#000', backgroundColor: 'transparent' }
                            }}
                        >
                            {t('all', '')}
                        </Button>
                        {categoryTypes.map((category) => (
                            <Button
                                key={category}
                                variant="text"
                                onClick={() => handleCategoryChange(category)}
                                size="small"
                                sx={{
                                    textTransform: 'capitalize',
                                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                    minWidth: { xs: 'auto', sm: '64px' },
                                    padding: { xs: '4px', sm: '6px 16px' },
                                    height: { xs: '28px', sm: '36px' },
                                    color: selectedCategory === category ? '#000' : '#ADADAD',
                                    textDecoration: selectedCategory === category ? 'underline' : 'none',
                                    '&:hover': { color: '#000', backgroundColor: 'transparent' },
                                }}
                            >
                                {t(category, '')}
                            </Button>
                        ))}
                    </Box>
                );

                return (
                    <>
                        {/* Inline bar — visible before scroll */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, md: 4 }, visibility: isSticky ? 'hidden' : 'visible' }}>
                            {filterButtons}
                        </Box>

                        {/* Fixed bar — appears in header row (desktop) or below header (mobile) when scrolled */}
                        {isSticky && ReactDOM.createPortal(
                            <Box
                                sx={{
                                    position: 'fixed',
                                    top: { xs: '52px', md: 0 },
                                    left: 0,
                                    right: 0,
                                    height: { xs: '60px', md: '90px' },
                                    zIndex: 1501,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: { xs: 'white', md: 'transparent' },
                                    pointerEvents: 'none',
                                }}
                            >
                                <Box sx={{ pointerEvents: 'auto' }}>
                                    {filterButtons}
                                </Box>
                            </Box>,
                            document.body
                        )}
                    </>
                );
            })()}

            {/* Gallery Grid - Masonry Layout */}
            <Box
                sx={{
                    columnCount: {
                        xs: 2,
                        sm: 3,
                    },
                    columnGap: 1,
                    minWidth: 0,
                    width: '100%'
                }}
            >
                {items.map((item, index) => {
                    const sizeClass = getSizeClass(index);

                    return (
                        <Box
                            key={`${item.id}-${index}`}
                            sx={{
                                breakInside: 'avoid',
                                marginBottom: 1
                            }}
                        >
                            <LazyLoadImage
                                item={item}
                                alt={item.title || `Gallery item ${index + 1}`}
                                index={index}
                                onImageClick={handleImageClick}
                                t={t}
                                sizeClass={sizeClass}
                            />
                        </Box>
                    );
                })}

                {/* Loading Skeleton Grid Items */}
                {loading && items.length === 0 && (
                    <>
                        {Array.from({ length: 12 }).map((_, index) => {
                            const sizeClass = getSizeClass(index);
                            const aspectRatio = sizeClass === 'wide' ? '2/1'
                                : sizeClass === 'wide-tall' ? '2/1.5'
                                    : sizeClass === 'wide-short' ? '2/0.75'
                                        : sizeClass === 'tall' ? '1/2'
                                            : '1/1';

                            return (
                                <Box
                                    key={`skeleton-${index}`}
                                    sx={{
                                        breakInside: 'avoid',
                                        marginBottom: 1
                                    }}
                                >
                                    <Box
                                        sx={{
                                            aspectRatio,
                                            borderRadius: { xs: 1, sm: 2 },
                                            overflow: 'hidden',
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            position: 'relative',
                                            width: '100%',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                                                transform: 'translateX(-100%)',
                                                animation: 'skeletonLoading 1.5s infinite'
                                            }
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </>
                )}
            </Box>

            {/* Loading More Skeleton Items (when loading additional items via scroll) */}
            {loading && items.length > 0 && (
                <Box
                    sx={{
                        columnCount: {
                            xs: 2,
                            sm: 3,
                        },
                        columnGap: {
                            xs: 0.75,
                            sm: 1,
                            md: 1.5,
                            lg: 2
                        },
                        minWidth: 0,
                        width: '100%',
                        mt: 2
                    }}
                >
                    {Array.from({ length: 6 }).map((_, index) => {
                        const sizeClass = getSizeClass(items.length + index);
                        const aspectRatio = sizeClass === 'wide' ? '2/1'
                            : sizeClass === 'wide-tall' ? '2/1.5'
                                : sizeClass === 'wide-short' ? '2/0.75'
                                    : sizeClass === 'tall' ? '1/2'
                                        : '1/1';

                        return (
                            <Box
                                key={`loading-more-skeleton-${index}`}
                                sx={{
                                    breakInside: 'avoid',
                                    marginBottom: 1
                                }}
                            >
                                <Box
                                    sx={{
                                        aspectRatio,
                                        borderRadius: { xs: 1, sm: 2 },
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        position: 'relative',
                                        width: '100%'
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Compact Loading Indicator */}
            {loading && items.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '60px',
                        mt: 2,
                        mb: 2,
                        gap: 1
                    }}
                >
                    <CircularProgress size={20} />
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        className="loading-text"
                        sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                    >
                        {t('loading_more', 'Loading more...')}
                    </Typography>
                </Box>
            )}

            {/* Load More Button (fallback if intersection observer fails) */}
            {!loading && hasMore && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Button
                        variant="outlined"
                        onClick={loadMore}
                        sx={{
                            minWidth: 120
                        }}
                    >
                        {t('load_more', 'Load More')}
                    </Button>
                </Box>
            )}

            {/* Intersection Observer Sentinel */}
            {hasMore && (
                <div
                    ref={sentinelRef}
                    style={{
                        height: '10px',
                        margin: '20px 0'
                    }}
                />
            )}

            {/* No Results */}
            {items.length === 0 && !loading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h6" color="textSecondary">
                        {t('no_items_in_category', 'No items found in this category')}
                    </Typography>
                </Box>
            )}

            {/* End of Results */}
            {/* {!hasMore && items.length > 0 && (
                <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                        {t('end_of_results', 'You have reached the end of the results')}
                    </Typography>
                </Box>
            )} */}

            {/* Error Message */}
            {error && items.length > 0 && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={loadMore}
                        sx={{ mt: 1 }}
                    >
                        {t('retry', 'Retry')}
                    </Button>
                </Box>
            )}

            {/* Image Carousel */}
            <ImageCarousel
                items={items}
                initialIndex={carouselIndex}
                open={carouselOpen}
                onClose={handleCarouselClose}
            />
        </Box>
    );
};

export default InfiniteGallery;